import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { ReactNode, useEffect } from "react";
import { useServiceKindList } from "../../services/hooks/use-service-kind-list";
import { useProcessesProcessCreateMutation } from "../api/process-api";
import { ServiceKind } from "@bitory-gmbh/bsm";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { v4 } from "uuid";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useAuthUserMeQuery } from "../../auth/api/user-api";
import { useListBranchQuery } from "../../branch/api/branch";

export function CreateProcessDropdown({
  companyId,
  onCreated,
  trigger,
}: {
  companyId: UUID;
  onCreated?: (processId: UUID, serviceKind: ServiceKind) => void;
  trigger?: ReactNode;
}) {
  const serviceKinds = useServiceKindList();
  const [create, { isLoading, error, reset }] =
    useProcessesProcessCreateMutation();
  const { user } = useAuth();
  // A process requires at least one branch. For a one-click creation we
  // infer it from the user instead of asking for it.
  const { branchIds, isLoading: isLoadingBranches } =
    useDefaultBranchIds(companyId);

  const doCreate = (serviceKind: ServiceKind) => {
    if (isLoading || user === null) return;

    if (branchIds.length === 0) return;

    create({
      id: v4(),
      serviceKind,
      companyId,
      branches: branchIds,
      owningUserId: user.id,
    })
      .unwrap()
      .then((process) => onCreated?.(process.id, process.serviceKind));
  };

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const disabled = isLoading || isLoadingBranches || branchIds.length === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || (
          <Button size="sm" disabled={disabled}>
            <Plus />
            {t("Vorgang")}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          <DropdownMenuLabel>{t("Dienstleistung")}</DropdownMenuLabel>
          {serviceKinds.map((serviceKind) => (
            <DropdownMenuItem
              key={serviceKind}
              onClick={() => doCreate(serviceKind)}
            >
              {t(serviceKind)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useDefaultBranchIds(companyId: UUID): {
  branchIds: UUID[];
  isLoading: boolean;
} {
  const { data: me, isLoading: isLoadingUser } = useAuthUserMeQuery({});
  const { data: list, isLoading: isLoadingBranches } = useListBranchQuery(
    { company: { active: true, values: [companyId] } },
    { skip: !companyId },
  );

  const isLoading = isLoadingUser || isLoadingBranches;
  const branches = list?.branches ?? [];
  const userBranchIds = me?.branches ?? [];

  const branch =
    branches.find((b) => userBranchIds.includes(b.id)) ?? branches[0];

  return { branchIds: branch ? [branch.id] : [], isLoading };
}
